import {
    IonBadge,
    IonBackButton,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardSubtitle,
    IonCardTitle,
    IonContent,
    IonDatetime,
    IonHeader,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonNote,
    IonPage,
    IonText,
    IonTitle,
    IonToggle,
    IonToolbar
} from "@ionic/react";
import {useParams} from "react-router-dom";
import React, {useEffect, useState, useCallback, useMemo} from "react";
import './AdminPersonalOverview.css'
import createAxiosInstance from "../../services/AxiosInstance";
import {Transaction} from "../../model/Transaction";
import {User} from "../../model/User";
import { useToast } from "../../utils/useToasts";
import TransactionCard from "../../components/transactions/TransactionCard";
import ProfilePicture from "../../components/shared/ProfilePicture";
import TagsInput from "../../components/shared/TagsInput";
import {APP_ROLES} from "../../auth/rbac";
import {getPushRegistrationSummary, isPushRegistrationActive} from "../../utils/pushRegistrationSummary";

const METHOD_NOT_ALLOWED_STATUS = 405;
const ISO_DATE_SLICE_END = 10;
const DETAIL_DATE_FORMATTER = new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
});

const PUSH_PLATFORM_LABELS: Record<string, string> = {
    web: 'Web',
    ios: 'iOS',
    android: 'Android',
};

const isNumericRouteParam = (value?: string): boolean => {
    return typeof value === 'string' && /^\d+$/.test(value);
};

const getTodayDateString = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const normalizeLocalDateParam = (value: string): string | null => {
    if (!value) return null;

    const directMatch = value.match(/^\d{4}-\d{2}-\d{2}/);
    if (directMatch) {
        return directMatch[0];
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return null;
    }

    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatPushDate = (value?: string): string => {
    if (!value) {
        return 'Nog niet bekend';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return 'Nog niet bekend';
    }

    return DETAIL_DATE_FORMATTER.format(parsed);
};

const getPushPlatformLabel = (platform?: string): string => {
    if (!platform) {
        return 'Onbekend apparaat';
    }

    return PUSH_PLATFORM_LABELS[platform.toLowerCase()] ?? platform;
};

const summarizeUserAgent = (userAgent?: string): string => {
    if (!userAgent) {
        return 'User agent volgt zodra het backend device-details meestuurt.';
    }

    const normalized = userAgent.replace(/\s+/g, ' ').trim();
    return normalized.length > 110 ? `${normalized.slice(0, 107)}...` : normalized;
};

const getRegistrationStatusLabel = (softDeleted?: boolean, enabled?: boolean): string => {
    if (softDeleted) {
        return 'Soft deleted';
    }

    if (enabled === false) {
        return 'Handmatig uitgezet';
    }

    return 'Beschikbaar voor push';
};

const AdminPersonalOverview: React.FC = () => {

    const [user, setUser] = useState<User>();
    const [transactions, setTransactions] = useState<Transaction[]>();
    const [currentBalance, setCurrentBalance] = useState<number>(0);
    const [remark, setRemarks] = useState<string>('Opwaarderen')
    const {playerId} = useParams<{ playerId?: string }>();
    const [newTransactionAmount, setNewTransactionAmount] = useState<string>('')
    const [selectedDate, setSelectedDate] = useState<string>(getTodayDateString());
    const [isManualCheckinOpen, setIsManualCheckinOpen] = useState<boolean>(false);
    const [isSavingTransaction, setIsSavingTransaction] = useState<boolean>(false);
    const [isUpdatingBlacklist, setIsUpdatingBlacklist] = useState<boolean>(false);
    const [isCheckingIn, setIsCheckingIn] = useState<boolean>(false);
    const showToasts = useToast();
    const axios = useMemo(() => createAxiosInstance(), []);

    const availableRoles = useMemo(() => [...APP_ROLES], []);
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [blackList, setBlackList] = useState<boolean>(false);
    const pushSummary = useMemo(() => getPushRegistrationSummary(user), [user]);

    const fetchUserData = useCallback(() => {
        if (!isNumericRouteParam(playerId)) {
            return;
        }

        axios.get(`/user/${playerId}`)
            .then(res => {
                setUser(res.data)
                setCurrentBalance(res.data.balance)
                setTransactions(Array.isArray(res.data.transactions) ? [...res.data.transactions].reverse() : [])
                setSelectedRoles(res.data.roles || [])
                setBlackList(!!res.data.blackList);
            })
    }, [axios, playerId]);
    const handleBlacklistToggle = (e: CustomEvent) => {
        const newValue = e.detail.checked;
        setBlackList(newValue);
        setIsUpdatingBlacklist(true);
        axios.patch(`/user/${playerId}/blacklist`, { blackList: newValue })
            .then(res => {
                setUser(res.data);
                setBlackList(!!res.data.blackList);
                showToasts('Zwarte lijst status bijgewerkt', 'success');
            })
            .catch(() => {
                setBlackList(user?.blackList ?? false);
                showToasts('Mislukt bijwerken van zwarte lijst status', 'danger');
            })
            .finally(() => {
                setIsUpdatingBlacklist(false);
            });
    };

    const updateRoles = useCallback((updatedRoles: string[]) => {
        if (!isNumericRouteParam(playerId)) {
            showToasts('Geen geldige speler geselecteerd', 'danger');
            return;
        }

        setSelectedRoles(updatedRoles);
        axios.patch(`/user/${playerId}/roles`, { roles: updatedRoles })
            .then(res => {
                console.log('Roles updated successfully:', res.data);
                setUser(res.data);
                showToasts('Rollen zijn bijgewerkt', 'success');
            })
            .catch(() => {
                setSelectedRoles(user?.roles || []);
                showToasts('Er is iets mis gegaan bij het bijwerken van rollen', 'danger');
            });
    }, [axios, playerId, showToasts, user?.roles]);

    const submitBalanceAdjustment = useCallback(() => {
        const parsedAmount = Number(newTransactionAmount);

        if (!isNumericRouteParam(playerId)) {
            showToasts('Geen speler geselecteerd', 'danger');
            return;
        }

        if (!newTransactionAmount.trim() || Number.isNaN(parsedAmount) || parsedAmount === 0) {
            showToasts('Vul een geldig bedrag in', 'danger');
            return;
        }

        const newTransaction: Transaction = {
            deltaBalance: parsedAmount,
            dateOfTransaction: new Date(),
            remark,
            isCheckIn: false
        }

        setIsSavingTransaction(true);
        axios.post(`/transactions/${playerId}`, newTransaction)
            .then(res => {
                fetchUserData()
                showToasts(`€ ${res.data.deltaBalance} is verwerkt`, 'success')
                setNewTransactionAmount('');
                setRemarks('Opwaarderen');
            })
            .catch(() => {
                showToasts('Saldo aanpassen mislukt', 'danger');
            })
            .finally(() => {
                setIsSavingTransaction(false);
            })
    }, [axios, fetchUserData, newTransactionAmount, playerId, remark, showToasts]);

    const handleTransactionKeyPress = (event: React.KeyboardEvent<HTMLIonInputElement>) => {
        if (event.key === 'Enter') {
            submitBalanceAdjustment();
        }
    };

    const handleSave = () => {
        const normalizedDate = normalizeLocalDateParam(selectedDate);

        if (!normalizedDate) {
            showToasts('Selecteer eerst een datum!', 'danger')
            return;
        }

        if (!isNumericRouteParam(playerId)) {
            showToasts('Geen speler geselecteerd', 'danger');
            return;
        }

        setIsCheckingIn(true);
        axios.post('/evening/check-in-manually', null, {
            params: {
                userId: playerId,
                date: normalizedDate
            }
        })
            .then(res => {
                fetchUserData()
                showToasts(`${res.data.name} is op ${res.data.checkInDate} ingecheckt!`, 'success')
                setIsManualCheckinOpen(false);
            })
            .catch(error => {
                if (error.request?.status === METHOD_NOT_ALLOWED_STATUS) {
                    showToasts('Al ingecheckt', 'danger')
                } else {
                    showToasts('Handmatige check-in mislukt', 'danger')
                }
            })
            .finally(() => {
                setIsCheckingIn(false);
            })
    };

    useEffect(() => {
        fetchUserData()
    }, [fetchUserData])

    const getItemLabel = useCallback((r: string) => r, []);
    const getItemKey = useCallback((r: string) => r, []);
    const handleDateChange = (nextValue: string | string[] | null | undefined) => {
        if (typeof nextValue === 'string') {
            setSelectedDate(nextValue.slice(0, ISO_DATE_SLICE_END));
            return;
        }

        if (Array.isArray(nextValue) && nextValue.length > 0 && typeof nextValue[0] === 'string') {
            setSelectedDate(nextValue[0].slice(0, ISO_DATE_SLICE_END));
        }
    };

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonBackButton/>
                    </IonButtons>
                    <IonTitle>Saldo aanpassen</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent>
                <IonCard className='user-card'>
                    <IonCardHeader>
                        <div className={'user-container'}>
                            {user && <ProfilePicture profilePicUrl={(user.profilePictureUrl?.length !== 0 && user.profilePictureUrl) ? `${process.env.REACT_APP_STORAGE}${user.profilePictureUrl}` : `${process.env.REACT_APP_STORAGE}default-profile.jpg`}
                                                     height={50}
                                                     width={50}
                                                     showEditable={false}
                                                     fullName={user.name}
                            />
                            }
                            <div className={'user-info-container'}>
                                <IonCardSubtitle>Account overzicht</IonCardSubtitle>
                                <IonCardTitle className='user-card-header'>{user?.name}</IonCardTitle>
                                <IonText color="light" className='user-card-header'>Saldo: € {currentBalance}</IonText>
                                <IonNote className='user-card-header'>Email: {user?.emailAddress != null ? user?.emailAddress : "Geen email ingevuld"}</IonNote>
                            </div>
                        </div>
                    </IonCardHeader>
                </IonCard>

                <IonCard>
                    <IonCardHeader>
                        <IonCardSubtitle>Financieel</IonCardSubtitle>
                        <IonCardTitle>Saldo aanpassen</IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                        <div className="admin-settings-stack">
                            <IonItem>
                                <IonLabel position="stacked">Saldo mutatie</IonLabel>
                                <IonInput
                                    value={newTransactionAmount}
                                    onKeyDown={handleTransactionKeyPress}
                                    onIonInput={(event) => setNewTransactionAmount(event.detail.value ?? '')}
                                    placeholder="Bijvoorbeeld 12.50 of -5"
                                    clearInput
                                    type="number"
                                />
                            </IonItem>
                            <IonItem>
                                <IonLabel position="stacked">Opmerking</IonLabel>
                                <IonInput
                                    value={remark}
                                    placeholder="Opmerking"
                                    onKeyDown={handleTransactionKeyPress}
                                    onIonInput={(event) => setRemarks(event.detail.value ?? '')}
                                    clearInput
                                />
                            </IonItem>
                            <IonButton expand="block" onClick={submitBalanceAdjustment} disabled={isSavingTransaction}>
                                {isSavingTransaction ? 'Opslaan...' : 'Saldo mutatie opslaan'}
                            </IonButton>
                        </div>
                    </IonCardContent>
                </IonCard>

                <IonCard>
                    <IonCardHeader>
                        <IonCardSubtitle>Toegang</IonCardSubtitle>
                        <IonCardTitle>Rollen en blokkades</IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                        <IonItem className="blacklist-item-space" lines="none">
                            <div className="settings-copy-block">
                                <IonLabel>Zwarte lijst</IonLabel>
                                <IonNote>Voor allerlei illegaliteiten.</IonNote>
                            </div>
                            <IonToggle
                                checked={blackList}
                                onIonChange={handleBlacklistToggle}
                                disabled={isUpdatingBlacklist}
                            />
                        </IonItem>
                    </IonCardContent>
                    <IonCardContent>
                        <TagsInput
                             selectedItems={selectedRoles}
                             onSelectedItemsChange={updateRoles}
                             availableItems={availableRoles}
                             getItemLabel={getItemLabel}
                             getItemKey={getItemKey}
                             label="Rollen"
                             placeholder="Zoek een rol..."
                             emptyStateText="Geen rollen geselecteerd"
                             noResultsText="Geen rollen gevonden"
                             maxSuggestions={6}
                             debounceMs={200}
                             portal={true}
                         />
                    </IonCardContent>
                </IonCard>

                <IonCard>
                    <IonCardHeader>
                        <IonCardSubtitle>Push notificaties</IonCardSubtitle>
                        <IonCardTitle>Apparaatstatus</IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                        <div className="push-admin-summary-grid">
                            <div className="push-admin-metric-tile">
                                <IonNote>Bekende apparaten</IonNote>
                                <strong>{pushSummary.totalRegistrations}</strong>
                            </div>
                            <div className="push-admin-metric-tile">
                                <IonNote>Actief</IonNote>
                                <strong>{pushSummary.enabledRegistrations}</strong>
                            </div>
                            <div className="push-admin-metric-tile">
                                <IonNote>Laatst gezien</IonNote>
                                <strong>{formatPushDate(pushSummary.latestSeenAt ?? undefined)}</strong>
                            </div>
                        </div>
                    </IonCardContent>
                    <IonCardContent>

                        {pushSummary.totalRegistrations === 0 && (
                            <div className="push-admin-empty-state">
                                <IonText>Deze gebruiker heeft nog geen gekoppelde push-apparaten.</IonText>
                            </div>
                        )}

                        {pushSummary.totalRegistrations > 0 && (
                            <div className="push-admin-device-list">
                                {pushSummary.registrations.map((registration, index) => {
                                    const isActive = isPushRegistrationActive(registration);

                                    return (
                                        <div className="push-admin-device-card" key={registration.id ?? registration.token ?? `${registration.platform}-${index}`}>
                                            <div className="push-admin-device-header">
                                                <div className="push-admin-device-title-block">
                                                    <IonText className="push-admin-device-platform">{getPushPlatformLabel(registration.platform)}</IonText>
                                                    <IonNote>{registration.token ? 'Token opgeslagen' : 'Token verborgen of nog niet meegestuurd'}</IonNote>
                                                </div>
                                                <IonBadge color={isActive ? 'success' : 'medium'}>
                                                    {isActive ? 'Actief' : 'Inactief'}
                                                </IonBadge>
                                            </div>
                                            <div className="push-admin-device-meta-grid">
                                                <div className="push-admin-device-field">
                                                    <IonNote>Laatst gezien</IonNote>
                                                    <strong>{formatPushDate(registration.lastSeenAt)}</strong>
                                                </div>
                                                <div className="push-admin-device-field">
                                                    <IonNote>Aangemaakt</IonNote>
                                                    <strong>{formatPushDate(registration.createdOn)}</strong>
                                                </div>
                                                <div className="push-admin-device-field">
                                                    <IonNote>Status</IonNote>
                                                    <strong>{getRegistrationStatusLabel(registration.softDeleted, registration.enabled)}</strong>
                                                </div>
                                            </div>
                                            <IonText color="medium" className="push-admin-user-agent">
                                                {summarizeUserAgent(registration.userAgent)}
                                            </IonText>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </IonCardContent>
                </IonCard>

                <IonCard className="manual-checkin-card">
                    <IonCardHeader>
                        <div className="manual-checkin-header">
                            <div>
                                <IonCardSubtitle>Activiteit</IonCardSubtitle>
                                <IonCardTitle>Handmatige check-in</IonCardTitle>
                                <IonNote className="manual-checkin-date-preview">Geselecteerd: {selectedDate}</IonNote>
                            </div>
                            <IonButton
                                fill="outline"
                                size="small"
                                className="manual-checkin-toggle"
                                onClick={() => setIsManualCheckinOpen(prev => !prev)}
                            >
                                {isManualCheckinOpen ? 'Inklappen' : 'Datum kiezen'}
                            </IonButton>
                        </div>
                    </IonCardHeader>
                    {isManualCheckinOpen && (
                        <IonCardContent>
                            <div className="manual-checkin-controls">
                                <div className="manual-checkin-datetime-wrap">
                                    <IonDatetime
                                        className="manual-checkin-datetime"
                                        value={selectedDate}
                                        presentation="date"
                                        firstDayOfWeek={1}
                                        locale="nl-NL"
                                        onIonChange={(event) => handleDateChange(event.detail.value)}
                                    />
                                </div>
                                <IonButton onClick={handleSave} disabled={isCheckingIn}>
                                    {isCheckingIn ? 'Opslaan...' : 'Check in'}
                                </IonButton>
                            </div>
                        </IonCardContent>
                    )}
                </IonCard>

                <IonCard>
                    <IonCardHeader>
                        <IonCardSubtitle>Historie</IonCardSubtitle>
                        <IonCardTitle>Laatste transacties</IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent className="transaction-history-card">
                        <IonList>
                            {transactions?.map((value: Transaction, key: any) => (
                                <TransactionCard key={key} amount={value.deltaBalance} description={value.remark}
                                                 date={value.dateOfTransaction} isTransaction={!value.isCheckIn}/>
                            ))}
                        </IonList>
                    </IonCardContent>
                </IonCard>
            </IonContent>
        </IonPage>

    );
}

export default AdminPersonalOverview;
